import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { setToken } from "../lib/api";
import { Loader2 } from "lucide-react";

/**
 * AuthCallback — handles the redirect from Emergent Google Auth.
 *
 * After a successful Google sign-in the user lands on our app with a URL
 * fragment like `#session_id=xxx`. This component:
 *   1. Reads session_id synchronously (once — StrictMode-safe via useRef)
 *   2. Exchanges it for an app JWT via POST /api/auth/google/callback
 *   3. Stores the JWT and navigates to admin dashboard (/) or employee portal (/portale)
 *   4. On error, shows a toast and sends the user back to /login
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
    const navigate = useNavigate();
    const hasProcessed = useRef(false);
    const [errorMsg, setErrorMsg] = useState("");

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const hash = window.location.hash || "";
        const match = hash.match(/session_id=([^&]+)/);
        const sessionId = match ? decodeURIComponent(match[1]) : "";

        // Clean the fragment out of the URL immediately so refreshes don't retry
        try {
            window.history.replaceState(null, "", window.location.pathname + window.location.search);
        } catch (_) {
            // ignore
        }

        if (!sessionId) {
            toast.error("Sessione Google mancante — riprova.");
            navigate("/accesso", { replace: true });
            return;
        }

        (async () => {
            try {
                const { data } = await api.post("/auth/google/callback", { session_id: sessionId });
                setToken(data.access_token);
                try { localStorage.setItem("ss_user_cache", JSON.stringify(data.user)); } catch (_) {}
                toast.success(`Bentornato/a, ${data.user.name || data.user.email}`);
                // Route based on role, honoring the entry point chosen before
                // the Google redirect (employee entry → portale, admin entry → gestionale)
                let entry = "";
                try {
                    entry = sessionStorage.getItem("ss_login_entry") || "";
                    sessionStorage.removeItem("ss_login_entry");
                } catch (_) {}
                let target;
                if (data.user.role === "admin") target = "/";
                else if (entry === "timbratura") target = "/timbratura";
                else if (data.user.role === "employee") target = "/portale";
                else target = entry === "admin" ? "/" : "/portale";
                // Full reload to reinitialise AuthProvider with the new token
                window.location.replace(target);
            } catch (e) {
                const detail = e?.response?.data?.detail;
                const msg = typeof detail === "string"
                    ? detail
                    : "Accesso con Google non riuscito. Riprova.";
                setErrorMsg(msg);
                toast.error(msg, { duration: 5000 });
                setTimeout(() => navigate("/accesso", { replace: true }), 1200);
            }
        })();
    }, [navigate]);

    return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-[#15498F] via-[#376DB3] to-[#5BAEDB] p-6 text-white">
            <Loader2 className="h-10 w-10 animate-spin" data-testid="auth-callback-loading" />
            <div className="mt-4 font-display text-lg font-semibold">Accesso in corso…</div>
            {errorMsg && (
                <div className="mt-2 max-w-md text-center text-sm text-white/85">{errorMsg}</div>
            )}
        </div>
    );
}
